import { TIMEZONES } from '../components/TimezoneSelector/timezoneData';
import { REVIEW_METHOD } from '../types/selectors';
import type { VideoCallConfirmation } from '../types/chat';
import type { ReviewMethodSelection } from '../types/selectors';

/**
 * Build a video-call confirmation card payload from a ReviewMethodSelection.
 * Returns null for non-video or incomplete selections.
 *
 * `selection.scheduledTime` is an ISO UTC string (e.g. `"2026-05-27T14:00:00Z"`)
 * emitted by `TimeSelector` (a Swan `TimePicker` whose `value` is `startTimeUTC`).
 * Both the displayed date and time are formatted in the user's IANA timezone so
 * the card shows the same wall-clock the customer picked.
 */
export function formatVideoConfirmation(
    selection: ReviewMethodSelection
): VideoCallConfirmation | null {
    if (
        selection.method !== REVIEW_METHOD.DesignLive ||
        !selection.scheduledDate ||
        !selection.scheduledTime ||
        !selection.timezone
    ) {
        return null;
    }

    return reconstructVideoConfirmation(selection.scheduledTime, selection.timezone);
}

/**
 * Derive a full VideoCallConfirmation display payload from just an ISO UTC instant.
 *
 * Used both by `formatVideoConfirmation` (at completion time, where we know the
 * user's selected timezone) and by `renderMessageView` when rehydrating chat
 * history that only persists `{requestType, appointmentTime}` (in which case we
 * fall back to the browser's current IANA timezone).
 */
export function reconstructVideoConfirmation(
    appointmentTime: string,
    timezoneKey?: string
): VideoCallConfirmation | null {
    const instant = new Date(appointmentTime);
    if (Number.isNaN(instant.getTime())) {
        return null;
    }

    const tz = timezoneKey || Intl.DateTimeFormat().resolvedOptions().timeZone;
    const tzEntry = TIMEZONES.find((t) => t.key === tz);
    const tzLabel = tzEntry ? `${tzEntry.city} (${tzEntry.abbr})` : tz;

    return {
        date: instant.toLocaleDateString('en-US', {
            weekday: 'long',
            month: 'long',
            day: 'numeric',
            year: 'numeric',
            timeZone: tz,
        }),
        time: instant.toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
            timeZone: tz,
        }),
        timezone: tzLabel,
        timezoneKey: tz,
        scheduledTimeUTC: appointmentTime,
    };
}
